import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { 
  ArrowLeft, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Activity,
  TrendingUp,
  BarChart3,
  Copy,
  Sun,
  Moon
} from 'lucide-react';
import { Monitor, PingLog, PublicMonitorGamification } from '../types';
import RelativeTime from './RelativeTime';
import { formatUptime, formatResponseTime, getStatusColor, getStatusIcon } from '../utils/api';

interface PublicMonitorStatusProps {
  monitor?: Monitor;
}

const PublicMonitorStatus: React.FC<PublicMonitorStatusProps> = ({ monitor: propMonitor }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [monitor, setMonitor] = useState<Monitor | null>(propMonitor || null);
  const [pingHistory, setPingHistory] = useState<PingLog[]>([]);
  const [gamification, setGamification] = useState<PublicMonitorGamification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  const loadMonitorDetails = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);

      // Load monitor details (public endpoint)
      const monitorResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3501'}/api/monitors/${id}/public`);
      if (!monitorResponse.ok) {
        throw new Error('Monitor not found or not publicly accessible');
      }
      const monitorData = await monitorResponse.json();
      console.log('Public monitor data:', monitorData.data);
      setMonitor(monitorData.data);

      // Load ping history (public endpoint)
      const historyResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3501'}/api/status/${id}/history/public?limit=100`);
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        console.log('Public ping history data:', historyData.data);
        setPingHistory(historyData.data.ping_history || []);
      }

      // Load gamification data (public endpoint)
      const gamificationResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3501'}/api/gamification/monitor/${id}/public`);
      if (gamificationResponse.ok) {
        const gamificationData = await gamificationResponse.json();
        console.log('Public gamification data:', gamificationData.data);
        setGamification(gamificationData.data);
      }

    } catch (err) {
      console.error('Error loading monitor details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load monitor details');
    } finally {
      setLoading(false);
    }
  };

  // Theme management
  useEffect(() => {
    // Initialize theme from localStorage or system preference
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');
    
    setTheme(initialTheme);
    document.documentElement.setAttribute('data-theme', initialTheme === 'dark' ? 'PingPalDark' : 'PingPal');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme === 'dark' ? 'PingPalDark' : 'PingPal');
    localStorage.setItem('theme', newTheme);
  };

  useEffect(() => {
    loadMonitorDetails();
  }, [id]);

  const calculateStats = () => {
    if (!pingHistory.length) return null;

    const totalPings = pingHistory.length;
    const successfulPings = pingHistory.filter(ping => ping.is_up).length;
    const failedPings = totalPings - successfulPings;
    const uptimePercentage = (successfulPings / totalPings) * 100;
    const averageResponseTime = pingHistory.reduce((sum, ping) => sum + ping.response_time, 0) / totalPings;
    const minResponseTime = Math.min(...pingHistory.map(ping => ping.response_time));
    const maxResponseTime = Math.max(...pingHistory.map(ping => ping.response_time));

    return {
      totalPings,
      successfulPings,
      failedPings,
      uptimePercentage,
      averageResponseTime,
      minResponseTime,
      maxResponseTime
    };
  };

  const copyShareLink = async () => {
    const shareUrl = `${window.location.origin}/public/monitor/${id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading monitor status...</p>
        </div>
      </div>
    );
  }

  if (error || !monitor) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="alert alert-error">
          <AlertCircle className="w-5 h-5" />
          <span>{error || 'Monitor not found'}</span>
        </div>
        <button onClick={() => navigate('/')} className="btn btn-outline mt-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>
      </div>
    );
  }

  const lastPing = pingHistory[0];
  const isCurrentlyUp = lastPing ? lastPing.is_up : false;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="btn btn-outline btn-sm">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div>
            <h1 className="text-3xl font-bold">{monitor.name}</h1>
            <p className="text-base-content/70">{monitor.url}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} className="btn btn-outline btn-sm">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button onClick={copyShareLink} className="btn btn-outline btn-sm">
            {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Share'}
          </button>
          <button onClick={loadMonitorDetails} className="btn btn-outline btn-sm">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className={`text-2xl ${isCurrentlyUp ? 'text-success' : 'text-error'}`}>
                {getStatusIcon(isCurrentlyUp)}
              </div>
              <div>
                <h3 className="card-title text-lg">Status</h3>
                <p className={`text-sm font-medium ${isCurrentlyUp ? 'text-success' : 'text-error'}`}>
                  {isCurrentlyUp ? 'Online' : 'Offline'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-primary" />
              <div>
                <h3 className="card-title text-lg">Uptime</h3>
                <p className="text-2xl font-bold">
                  {stats ? formatUptime(stats.uptimePercentage) : '0.00%'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <Activity className="w-6 h-6 text-primary" />
              <div>
                <h3 className="card-title text-lg">Response Time</h3>
                <p className="text-2xl font-bold">
                  {stats ? formatResponseTime(stats.averageResponseTime) : '0ms'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-primary" />
              <div>
                <h3 className="card-title text-lg">Last Ping</h3>
                <p className="text-sm">
                  {lastPing ? (
                    <RelativeTime dateString={lastPing.created_at} />
                  ) : (
                    'Never'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Statistics */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                     <div className="card bg-base-100 shadow-lg">
             <div className="card-body">
               <h3 className="card-title">
                 <BarChart3 className="w-5 h-5" />
                 Ping Statistics
               </h3>
               <div className="space-y-3">
                 <div className="flex justify-between">
                   <span>Total Pings:</span>
                   <span className="font-medium">{stats.totalPings}</span>
                 </div>
                 <div className="flex justify-between">
                   <span>Successful:</span>
                   <span className="font-medium text-success">{stats.successfulPings}</span>
                 </div>
                 <div className="flex justify-between">
                   <span>Failed:</span>
                   <span className="font-medium text-error">{stats.failedPings}</span>
                 </div>
                 <div className="flex justify-between">
                   <span>Min Response:</span>
                   <span className="font-medium">{formatResponseTime(stats.minResponseTime)}</span>
                 </div>
                 <div className="flex justify-between">
                   <span>Max Response:</span>
                   <span className="font-medium">{formatResponseTime(stats.maxResponseTime)}</span>
                 </div>
                 
                 {/* Streak Information */}
                 {(gamification?.current_streak !== undefined || gamification?.best_streak !== undefined) && (
                   <>
                     <div className="divider">Streaks</div>
                     <div className="flex justify-between">
                       <span>Current Streak:</span>
                       <span className="font-medium text-primary">{gamification?.current_streak || 0}</span>
                     </div>
                     <div className="flex justify-between">
                       <span>Best Streak:</span>
                       <span className="font-medium text-primary">{gamification?.best_streak || 0}</span>
                     </div>
                   </>
                 )}
               </div>
             </div>
           </div>

                     {/* Gamification Stats */}
           {gamification && (
             <div className="card bg-base-100 shadow-lg">
               <div className="card-body">
                 <h3 className="card-title">
                   <TrendingUp className="w-5 h-5" />
                   Achievements ({gamification.achievements?.length || 0})
                 </h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                   {gamification.achievements?.map((achievement: any) => (
                     <div key={achievement.id} className="p-4 bg-base-200 rounded-lg">
                       <div className="flex items-center gap-3">
                         <div className="text-2xl">{achievement.icon}</div>
                         <div>
                           <div className="font-semibold">{achievement.title}</div>
                           <div className="text-sm opacity-70">{achievement.description}</div>
                           <div className="text-xs opacity-50"><RelativeTime dateString={achievement.achieved_at} /></div>
                         </div>
                       </div>
                     </div>
                   ))}
                   
                   {gamification.achievements?.length === 0 && (
                     <div className="col-span-full text-center p-4 text-base-content/50">
                       <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                       <p>No achievements yet</p>
                     </div>
                   )}
                 </div>
               </div>
             </div>
           )}
        </div>
      )}

      {/* Recent Ping History */}
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h3 className="card-title">
            <Activity className="w-5 h-5" />
            Recent Ping History
          </h3>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Response Time</th>
                  <th>Status Code</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {pingHistory.slice(0, 20).map((ping, index) => (
                  <tr key={index}>
                    <td>
                      <span className={`badge ${getStatusColor(ping.is_up)}`}>
                        {ping.is_up ? 'UP' : 'DOWN'}
                      </span>
                    </td>
                    <td>{formatResponseTime(ping.response_time)}</td>
                    <td>{ping.status_code || 'N/A'}</td>
                    <td>
                      <RelativeTime dateString={ping.created_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-8 text-base-content/70">
        <p>Powered by PingPal - Uptime Monitoring Service</p>
        <p className="text-sm">Last updated: <RelativeTime dateString={new Date().toISOString()} /></p>
      </div>
    </div>
  );
};

export default PublicMonitorStatus; 